import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, Star, Download, CheckCircle2, ArrowRight } from 'lucide-react';
import { useBranding, buildBrandingUrl } from '../contexts/BrandingContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const BG_IMAGE = 'https://customer-assets.emergentagent.com/job_4dc508e2-00ad-4791-a42f-c0685c15cd50/artifacts/w7lm448x_background.png';

const features = [
  'Personalised nutrition & diet plans',
  'Track calories, water & macros daily',
  'AI-powered meal recommendations',
  'Expert chat with nutritionists',
];

export default function DownloadPage() {
  const { branding } = useBranding();
  const logoSrc = buildBrandingUrl(branding?.app_logo || branding?.logo_url);

  const [links, setLinks] = useState({ android_app_url: '', app_store_url: '' });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setTimeout(() => setMounted(true), 60);
    axios.get(`${API}/api/v1/public/app-links`)
      .then(r => setLinks(r.data))
      .catch(() => {});
  }, []);

  return (
    <div
      data-testid="download-page"
      style={{
        minHeight: '100vh',
        backgroundImage: `url(${BG_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        padding: '32px 16px',
        fontFamily: "'Nunito', 'Inter', sans-serif",
      }}
    >
      {/* subtle overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'rgba(8, 8, 14, 0.3)',
        pointerEvents: 'none',
      }} />

      {/* Card */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 440,
          background: 'rgba(255,255,255,0.93)',
          backdropFilter: 'blur(28px)',
          WebkitBackdropFilter: 'blur(28px)',
          borderRadius: 24,
          padding: '40px 36px 36px',
          boxShadow: '0 8px 48px rgba(0,0,0,0.32), 0 2px 8px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,1)',
          border: '1px solid rgba(255,255,255,0.65)',
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0) scale(1)' : 'translateY(18px) scale(0.97)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
          textAlign: 'center',
        }}
      >
        {/* Logo */}
        <div style={{ marginBottom: 16 }}>
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={branding?.brand_name || 'Logo'}
              style={{ height: 40, maxWidth: 140, objectFit: 'contain' }}
            />
          ) : (
            <span style={{
              fontSize: 11, fontWeight: 800, letterSpacing: '3px',
              color: '#6d53e0', textTransform: 'uppercase',
            }}>
              {branding?.brand_name || 'WINGSDIET'}
            </span>
          )}
        </div>

        {/* Pill badge */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
          <span style={{
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            color: '#16a34a',
            fontSize: 11,
            fontWeight: 700,
            padding: '5px 14px',
            borderRadius: 999,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            letterSpacing: '0.2px',
          }}>
            <CheckCircle2 size={12} strokeWidth={2.5} />
            Account Created Successfully
          </span>
        </div>

        {/* Heading */}
        <h1 style={{
          fontWeight: 900,
          fontSize: 26,
          lineHeight: 1.18,
          color: '#0f0f1a',
          textAlign: 'center',
          margin: '0 0 6px',
          letterSpacing: '-0.3px',
          textTransform: 'uppercase',
        }}>
          YOU'RE ALL SET!<br />DOWNLOAD THE APP
        </h1>
        <p style={{
          textAlign: 'center', color: '#6b7280',
          fontSize: 13, marginBottom: 24, lineHeight: 1.55,
        }}>
          Get the full experience on your phone and start your journey today
        </p>

        {/* Features */}
        <div style={{
          background: '#fafafa',
          border: '1px solid #f3f4f6',
          borderRadius: 14,
          padding: '14px 16px',
          marginBottom: 24,
          textAlign: 'left',
        }}>
          {features.map((f, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '7px 0',
                borderBottom: i < features.length - 1 ? '1px solid #f3f4f6' : 'none',
                opacity: mounted ? 1 : 0,
                transition: `opacity 0.4s ease ${0.25 + i * 0.08}s`,
              }}
            >
              <div style={{
                width: 20, height: 20, borderRadius: '50%',
                background: '#ede9fe',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <ArrowRight size={11} color="#6d53e0" strokeWidth={2.5} />
              </div>
              <span style={{ color: '#374151', fontSize: 13, fontWeight: 500 }}>{f}</span>
            </div>
          ))}
        </div>

        {/* Download buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {links.android_app_url ? (
            <a
              href={links.android_app_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="android-download-btn"
              style={btnStyle('#1a1a2e', '#0f3460')}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 24px rgba(10,20,60,0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(10,20,60,0.3)'; }}
            >
              <div style={btnIconWrap('#2d2d4e')}>
                <Smartphone size={18} color="#a5b4fc" />
              </div>
              <div style={{ textAlign: 'left', flex: 1 }}>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Download on</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', letterSpacing: '-0.2px' }}>Google Play</div>
              </div>
              <Download size={15} color="rgba(255,255,255,0.45)" />
            </a>
          ) : (
            <div data-testid="android-download-btn-disabled" style={disabledBtnStyle}>
              <div style={btnIconWrap('#e5e7eb')}>
                <Smartphone size={18} color="#9ca3af" />
              </div>
              <div style={{ textAlign: 'left', flex: 1 }}>
                <div style={{ fontSize: 10, color: '#9ca3af', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Coming soon</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#6b7280' }}>Google Play</div>
              </div>
            </div>
          )}

          {links.app_store_url ? (
            <a
              href={links.app_store_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ios-download-btn"
              style={btnStyle('#16213e', '#1a1a2e')}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 24px rgba(10,20,60,0.5)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(10,20,60,0.3)'; }}
            >
              <div style={btnIconWrap('#2d2d4e')}>
                <Star size={18} color="#c4b5fd" fill="#c4b5fd" />
              </div>
              <div style={{ textAlign: 'left', flex: 1 }}>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Download on the</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', letterSpacing: '-0.2px' }}>App Store</div>
              </div>
              <Download size={15} color="rgba(255,255,255,0.45)" />
            </a>
          ) : (
            <div data-testid="ios-download-btn-disabled" style={disabledBtnStyle}>
              <div style={btnIconWrap('#e5e7eb')}>
                <Star size={18} color="#9ca3af" />
              </div>
              <div style={{ textAlign: 'left', flex: 1 }}>
                <div style={{ fontSize: 10, color: '#9ca3af', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Coming soon</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#6b7280' }}>App Store</div>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

const btnStyle = (c1, c2) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '13px 16px',
  background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`,
  borderRadius: 12,
  textDecoration: 'none',
  boxShadow: '0 4px 16px rgba(10,20,60,0.3)',
  transition: 'box-shadow 0.2s ease',
  cursor: 'pointer',
  border: 'none',
});

const disabledBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '13px 16px',
  background: '#f9fafb',
  border: '1.5px solid #e5e7eb',
  borderRadius: 12,
  cursor: 'default',
};

const btnIconWrap = (bg) => ({
  width: 38,
  height: 38,
  borderRadius: 10,
  background: bg,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});
